"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "@/navigation";
import { useLocale } from "next-intl";
import FrontBannerRenderer, { type BannerHeadingLevel } from "@/components/Banner/FrontBannerRenderer";
import type { FrontBannerV2PageConfig } from "@/lib/api/frontBanner";
import { fetchFrontBannerV2Client, hasRenderableFrontBannerPageConfig } from "@/lib/api/frontBanner";
import { matchFrontBannerPageConfig } from "@/lib/banner/resolveBannerSlug";

interface TabItem {
	label: string;
	link?: string;
	active?: boolean;
}

interface BreadcrumbItem {
	label: string;
	link?: string;
}

interface CommonTaileProps {
	msg?: string;
	description?: string | null;
	tabs?: TabItem[];
	breadcrumbItems?: BreadcrumbItem[];
	backgroundImage?: string;
	isShowImage?: boolean;
	pageSlug?: string;
	headingLevel?: BannerHeadingLevel;
}

export default function CommonTaile({ 
	isShowImage = true,
	msg = "", 
	description = null,
	tabs = [],
	breadcrumbItems = [],
	backgroundImage = "/image/plsyy/product-banner.webp",
	pageSlug = "",
	headingLevel = "h1",
}: CommonTaileProps) {
	const locale = useLocale();
	const [pageConfig, setPageConfig] = useState<FrontBannerV2PageConfig | null>(null);
	const normalizedPageSlug = useMemo(() => pageSlug.trim(), [pageSlug]);
	const hasResolvedBanner = hasRenderableFrontBannerPageConfig(pageConfig);

	useEffect(() => {
		let isActive = true;

		if (!normalizedPageSlug) {
			setPageConfig(null);
			return () => {
				isActive = false;
			};
		}

		fetchFrontBannerV2Client(locale)
			.then((payload) => {
				if (!isActive) {
					return;
				}

				setPageConfig(matchFrontBannerPageConfig(payload, normalizedPageSlug));
			})
			.catch(() => {
				if (isActive) {
					setPageConfig(null);
				}
			});

		return () => {
			isActive = false;
		};
	}, [locale, normalizedPageSlug]);

	const HeadingTag = headingLevel;

	return (
		<div className="relative">
			{hasResolvedBanner ? (
				<FrontBannerRenderer pageConfig={pageConfig} headingLevel={headingLevel} />
			) : isShowImage ? (
			<>
			<style dangerouslySetInnerHTML={{__html: `
				@keyframes bannerZoomOut {
					from {
						transform: scale(1.25);
						opacity: 0.95;
					}
					to {
						transform: scale(1);
						opacity: 1;
					}
				}
				.banner-zoom-out {
					animation: bannerZoomOut 3s ease-in-out forwards;
					transform-origin: center center;
				}
			`}} />
			{/* Banner 区域 - 手机端给足高度，背景图撑满整块（含标题和文字） */}
			<div className={`relative w-full overflow-hidden !pt-[75px] max-md:pt-0 ${isShowImage ? 'h-[50vh] max-md:h-[20vh]' : ''}`}>
				{/* 背景图 - 绝对定位填满容器，object-cover 撑满底部标题和文字 */}
				<img 
					src={backgroundImage}
					alt={msg || "Banner"}
					className="absolute inset-0 w-full h-full object-cover object-center banner-zoom-out"
				/>
				<div className="absolute inset-0 bg-mainColor/35"></div>
				{/* 文字内容 - 绝对定位在图片上方 */}
				<div className="absolute inset-0 flex flex-col items-start justify-center">
				<div className="container mx-auto relative flex flex-col">
						{/* 标题内容 */}
						<div className="relative flex flex-col">
							{/* 背景水印文字 - 重叠在蓝色横线和标题正下方 */}
							{msg && (
								<div className="absolute text-[36px] max-md:text-[36px] md:text-[80px] lg:text-[90px] font-bold text-white/20 select-none pointer-events-none left-0 top-0 leading-none z-0 whitespace-nowrap">
									{msg.toUpperCase()}
								</div>
							)}
							
							{/* 蓝色横线 */}
							<div className="relative z-20 mb-4">
								<div 
									className="h-1.5 shadow-lg bg-mainColor w-12"
								></div>
							</div>
							
							{/* 标题容器 */}
							<div className="relative z-10">
								{/* 主标题 */}
								{headingLevel === "h2" ? (
									<h2
										className="text-4xl md:text-5xl 2xl:text-6xl font-bold text-white tracking-tight max-md:text-3xl text-left leading-tight"
										style={{
											textShadow: `
												1px 1px 0px rgba(0, 0, 0, 0.1),
												2px 2px 0px rgba(0, 0, 0, 0.1),
												3px 3px 0px rgba(0, 0, 0, 0.15),
												4px 4px 0px rgba(0, 0, 0, 0.1),
												5px 5px 0px rgba(0, 0, 0, 0.15),
												6px 6px 0px rgba(0, 0, 0, 0.1),
												7px 7px 0px rgba(0, 0, 0, 0.05),
												8px 8px 0px rgba(0, 0, 0, 0.05),
												10px 10px 15px rgba(0, 0, 0, 0.1),
												12px 12px 20px rgba(0, 0, 0, 0.15),
												0 0 15px rgba(255, 255, 255, 0.05),
												0 0 25px rgba(255, 255, 255, 0.05)
											`
										}}
									>
										{msg}
									</h2>
								) : headingLevel === "div" ? (
									<div
										className="text-4xl md:text-5xl 2xl:text-6xl font-bold text-white tracking-tight max-md:text-3xl text-left leading-tight"
										style={{
											textShadow: `
												1px 1px 0px rgba(0, 0, 0, 0.1),
												2px 2px 0px rgba(0, 0, 0, 0.1),
												3px 3px 0px rgba(0, 0, 0, 0.15),
												4px 4px 0px rgba(0, 0, 0, 0.1),
												5px 5px 0px rgba(0, 0, 0, 0.15),
												6px 6px 0px rgba(0, 0, 0, 0.1),
												7px 7px 0px rgba(0, 0, 0, 0.05),
												8px 8px 0px rgba(0, 0, 0, 0.05),
												10px 10px 15px rgba(0, 0, 0, 0.1),
												12px 12px 20px rgba(0, 0, 0, 0.15),
												0 0 15px rgba(255, 255, 255, 0.05),
												0 0 25px rgba(255, 255, 255, 0.05)
											`
										}}
									>
										{msg}
									</div>
								) : (
								<h1 
									className="text-4xl md:text-5xl 2xl:text-6xl font-bold text-white tracking-tight max-md:text-3xl text-left leading-tight"
									style={{
										textShadow: `
											1px 1px 0px rgba(0, 0, 0, 0.1),
											2px 2px 0px rgba(0, 0, 0, 0.1),
											3px 3px 0px rgba(0, 0, 0, 0.15),
											4px 4px 0px rgba(0, 0, 0, 0.1),
											5px 5px 0px rgba(0, 0, 0, 0.15),
											6px 6px 0px rgba(0, 0, 0, 0.1),
											7px 7px 0px rgba(0, 0, 0, 0.05),
											8px 8px 0px rgba(0, 0, 0, 0.05),
											10px 10px 15px rgba(0, 0, 0, 0.1),
											12px 12px 20px rgba(0, 0, 0, 0.15),
											0 0 15px rgba(255, 255, 255, 0.05),
											0 0 25px rgba(255, 255, 255, 0.05)
										`
									}}
								>
									{msg}
								</h1>
								)}
							</div>

							{description && (
								<p className="mt-4 line-clamp-3 max-w-6xl text-base leading-relaxed text-white/90 text-left relative z-10">{description}</p>
							)}
						</div>
					</div>
				</div>
			</div>
			</>
			) : msg ? (
				<HeadingTag className="sr-only">{msg}</HeadingTag>
			) : null}

			{/* 底部导航栏 */}
			{(tabs.length > 0 || breadcrumbItems.length > 0) && (
				<div className={`relative bg-mainColor/10 border-t border-gray-200 shadow-sm ${isShowImage ? '' : 'md:pt-[75px]'}`}>
					<div className="container max-md:w-full mx-auto">
						<div className="relative flex items-center justify-between py-3 sm:py-4 max-md:flex-col max-md:items-start max-md:gap-3 max-md:py-3 w-full">
							{breadcrumbItems.length > 0 && (
								<div className="flex items-center gap-1.5 sm:gap-2 flex-wrap max-md:text-sm">
									<i className="ri-home-8-fill text-lg sm:text-xl text-mainColor max-md:text-base"></i>
									{breadcrumbItems.map((item, index) => (
										<React.Fragment key={index}>
											{item.link && item.link.trim() !== "" ? (
												<>
													<Link href={item.link} className="!text-black hover:!text-mainColor hover:underline cursor-pointer transition-colors text-sm sm:text-base md:text-lg max-md:text-xs">
														{item.label}
													</Link>
													{index !== breadcrumbItems.length - 1 && <span className="text-black text-sm sm:text-base md:text-lg max-md:text-xs">/</span>}
												</>
											) : (
												<>
													<span className="!text-mainColor cursor-default select-none text-sm sm:text-base md:text-lg max-md:text-xs">{item.label}</span>
													{index !== breadcrumbItems.length - 1 && <span className="text-black text-sm sm:text-base md:text-lg max-md:text-xs">/</span>}
												</>
											)}
										</React.Fragment>
									))}
								</div>
							)}

							{tabs.length > 0 && (
								<div className="flex items-center gap-6 sm:gap-8 md:gap-10 max-md:flex-wrap max-md:gap-3 max-md:w-full">
									{tabs.map((tab, index) => (
											<Link
												key={index}
												href={tab.link}
												className={`group relative text-gray-800 transition-colors duration-300 text-base sm:text-lg md:text-xl max-md:text-sm ${
													tab.active ? "text-mainColor" : "hover:text-mainColor"
												}`}
											>
												{tab.label}
												<span 
													className={`absolute left-0 bg-mainColor transition-all duration-300 ${
														tab.active 
															? "w-full" 
															: "w-0 group-hover:w-full"
													}`}
													style={{ bottom: '-0.5rem', height: '2px' }}
												></span>
											</Link>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
